import React, { FC, MouseEvent, PropsWithChildren, useEffect, useState } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { BaseStyle } from '@nhl/nhl-breakaway-ui';

import { PlayoffSeriesGame } from '~/views/pages/playoffSeries/types';
import { getEventDetails } from '~/views/pages/schedule/components/listView/components/dayOverview/utils';
import {
    ActionPopover,
    CardActionContainer,
    CardDescription,
    CardIcon,
    CardItem,
    EventContainerSmall,
    EventIndicatorSmall,
} from '~/views/pages/schedule/styles';
import { GameLink, ScheduleGame } from '~/views/pages/schedule/types';

import { IconContainer, Item, LargeContainer, PopperContent, SmallContainer } from './styles';

type Props = {
    game: ScheduleGame | PlayoffSeriesGame;
    gameLinks: Array<GameLink>;
    isPlayoffSeriesPage?: boolean;
};

type WrapperProps = PropsWithChildren & Props;

export const GameLinksPopper: FC<Props> = ({ gameLinks, isPlayoffSeriesPage = false }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorElement(event.currentTarget);
        setIsOpen(true);
    };

    const handleClose = () => {
        setAnchorElement(null);
        setIsOpen(false);
    };

    useEffect(() => {
        const resizeListener = () => {
            setAnchorElement(null);
        };
        if (anchorElement) {
            window.addEventListener('resize', resizeListener);
        }
        return () => window.removeEventListener('resize', resizeListener);
    }, [anchorElement]);

    const id = isOpen ? 'simple-popover' : undefined;
    return (
        <>
            <IconContainer $isDarkPage={isPlayoffSeriesPage} onClick={handleClick}>
                <MoreHorizIcon />
            </IconContainer>
            <ActionPopover
                id={id}
                open={isOpen}
                anchorEl={anchorElement}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                {/**
                 * By default, Material UI adds the popover to the document.body. However the document.body isn't
                 * wrapped in RootStyle, so we do it here. You could specify the Schedule container but this would be
                 * gross passing this down so many levels
                 */}
                <BaseStyle.RootStyle>
                    <PopperContent>
                        {gameLinks.map((gameLink) => (
                            <CardItem key={gameLink.actionUrl} href={gameLink.actionUrl} onClick={handleClose}>
                                {!isPlayoffSeriesPage && <CardIcon>{gameLink.icon && gameLink.icon}</CardIcon>}
                                <CardDescription $isPlayoffSeriesPage={isPlayoffSeriesPage}>
                                    {gameLink.message}
                                </CardDescription>
                                {!isPlayoffSeriesPage && (
                                    <CardActionContainer>
                                        <ChevronRightIcon />
                                    </CardActionContainer>
                                )}
                            </CardItem>
                        ))}
                    </PopperContent>
                </BaseStyle.RootStyle>
            </ActionPopover>
        </>
    );
};

export const GameLinksPopperWrapper: FC<WrapperProps> = ({
    children,
    game,
    gameLinks,
    isPlayoffSeriesPage = false,
}) => {
    const eventDetails = getEventDetails(game as ScheduleGame);
    return (
        <>
            <LargeContainer>{children}</LargeContainer>
            <SmallContainer>
                <Item>
                    {!isPlayoffSeriesPage && eventDetails.eventType && (
                        <EventContainerSmall>
                            <EventIndicatorSmall $eventType={eventDetails.eventType} />
                        </EventContainerSmall>
                    )}
                </Item>
                <Item $align="center">{children}</Item>
                <Item $align="right">
                    <GameLinksPopper game={game} gameLinks={gameLinks} isPlayoffSeriesPage={isPlayoffSeriesPage} />
                </Item>
            </SmallContainer>
        </>
    );
};
